import React, { useEffect, useRef, useState } from 'react';
import { AppPage } from '../layouts/types';
import { ProfileFormSchema } from '../zod.schema';
import { z } from "zod";
import ApplicationService from '../services/Application';
import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar } from '@radix-ui/themes';
import { useForm, FieldValues } from 'react-hook-form';
import { AiOutlineMail } from 'react-icons/ai';
import { VscEdit } from 'react-icons/vsc';
import { useDispatch, useSelector } from 'react-redux';
import { BackNavigation } from '../components/shared';
import axiosInstance from '../config/axios';
import IAgeGroup from '../interfaces/AgeGroup';
import ICountry from '../interfaces/Country';
import IIndustry from '../interfaces/Industry';
import { transformUser } from '../middlewares/transform/transformerLogin';
import { uploadImages } from '../services/aws-upload';
import { AppDispatch, RootState } from '../store';
import { Error, Success } from '../components/alert';
import { getProfileData, updateProfilePhoto } from 'store/reducers/authentication';
import calculateFilledPercentage from 'utils/calculateFilledPercentage';

type FormData = z.infer<typeof ProfileFormSchema>;
interface IProfileData {
  first_name?: string;
  last_name?: string;
  linkedinLink?: string;
  hobbies?: string;
  short_bio?: string;
  age_group?: string;
  country?: string;
  town_city?: string;
  profile_photo?: string;
  trainee_email?: string;
}

const Profile: AppPage = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [countries, setCountries] = useState<ICountry[]>([]);
  const [ageGroups, setAgeGroups] = useState<IAgeGroup[]>([]);
  const [industries, setIndustries] = useState<IIndustry[]>([]);
  const [profileImage, setProfileImage] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [imageFile, setImageFile] = useState<File>();
  const appService = new ApplicationService();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isErrorAlert, setIsErrorAlert] = useState(false);

  const dispatch: AppDispatch = useDispatch();
  const auth: any = useSelector((state: RootState) => state.auth.user);
  const profilePhoto = useSelector((state: RootState) => state.auth.profilePhoto);
  const profileData: any = useSelector((state: RootState) => state.auth.profileData);


  useEffect(() => {
    appService
      .getCountryList()
      .then((countryList) => setCountries(countryList));

    appService
      .getAgeGroupList()
      .then((ageGroupList) => setAgeGroups(ageGroupList));
    appService
      .getIndustryList()
      .then((industryList) => setIndustries(industryList));
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(ProfileFormSchema),
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const profileData = await appService.getProfileData();
        if (Array.isArray(profileData)) {
          setValue("firstName", auth?.first_name || auth?.firstName || "");
          setValue("lastName", auth?.last_name || auth?.lastName || "");
          setValue("linkedIn", auth?.linkedinLink || auth?.linkedin || "");
          setValue("profileMessage", auth?.hobbies || auth?.profileMessage || "");
          setValue("aboutMe", auth?.short_bio || auth?.aboutMe || "");
          setValue("ageGroup", auth?.age_group || auth?.ageGroup || "");
          setValue("country", auth?.country || "");
          setValue("city", auth?.town_city || auth?.city || "");
          setProfileImage(auth?.profile_photo || auth?.profileImage || "");
          setEmail(auth?.trainee_email || auth?.email || "");
        } else {
          const data = profileData as IProfileData;
          setValue("firstName", data.first_name || "");
          setValue("lastName", data.last_name || "");
          setValue("linkedIn", data.linkedinLink || "");
          setValue("profileMessage", data.hobbies || "");
          setValue("aboutMe", data.short_bio || "");
          setValue("ageGroup", data.age_group || "");
          setValue("country", data.country || "");
          setValue("city", data.town_city || "");
          setProfileImage(data.profile_photo || "");
          setEmail(data.trainee_email || "");
        }
      } catch (error) {
        console.error("Failed to fetch profile data:", error);
        setIsErrorAlert(true);
        setValue("firstName", auth?.first_name || auth?.firstName || "");
        setValue("lastName", auth?.last_name || auth?.lastName || "");
        setValue("linkedIn", auth?.linkedinLink || auth?.linkedin || "");
        setValue("profileMessage", auth?.hobbies || auth?.profileMessage || "");
        setValue("aboutMe", auth?.short_bio || auth?.aboutMe || "");
        setValue("ageGroup", auth?.age_group || auth?.ageGroup || "");
        setValue("country", auth?.country || "");
        setValue("city", auth?.town_city || auth?.city || "");
        setProfileImage(auth?.profile_photo || auth?.profileImage || "");
        setEmail(auth?.trainee_email || auth?.email || "");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [auth, setValue]);

  useEffect(() => {
    if (profileData) {
      calculateFilledPercentage(profileData);
    }
  }, [profileData]);

  // useEffect(() => {
  //   console.log("Profile Image:", profileImage);

  // }, [profileImage]);

  const handleUpdateProfileImage = async (s3ImageUrl: string) => {
    try {
      await axiosInstance.post('/trainee/update_trainee_profile_image', {
        profilePhoto: s3ImageUrl,
      });
    } catch (error) {
      console.error('Failed to update profile image:', error);
      setIsErrorAlert(true);
    }
  };

  const onSubmit = async (data: FieldValues) => {
    setLoading(true);

    const requestData: any = {
      query: data.query,
      last_name: data.lastName,
      first_name: data.firstName,
      age_group: data.ageGroup,
      town_city: data.city,
      country: data.country,
      linkedinLink: data.linkedIn,
      short_bio: data.aboutMe,
      hobbies: data.profileMessage,
    };

    // if (imageFile) {
    //   try {
    //     const s3Url = await uploadImages(imageFile);
    //     requestData['profile_photo'] = s3Url;
    //     setProfileImage(s3Url);
    //     await handleUpdateProfileImage(s3Url);
    //   } catch (error) {
    //     console.error('Failed to upload image:', error);
    //     setIsErrorAlert(true);
    //     setLoading(false);
    //     return;
    //   }
    // }

    try {
      const response: any = await axiosInstance.post(
        "/trainee/save_trainee_profile_data",
        requestData
      );
      console.log(response);
      setLoading(false);
      setIsOpen(true);
      let updateAuth = transformUser(response.data.trainee);
      if (updateAuth.profileImage?.includes("localhost")) {
        updateAuth.profileImage = auth?.profileImage;
      }

      if (response.code === 200) {
        // dispatch(loginSuccess(updateAuth as User));
        dispatch(getProfileData());
        localStorage.setItem("userData", JSON.stringify(updateAuth));
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      setIsErrorAlert(true);
    }
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      setImageFile(file);
      setLoading(true);
      try {
        const s3Url = await uploadImages(file);
        setProfileImage(s3Url);
        dispatch(updateProfilePhoto({ profilePhoto: s3Url }));
        await handleUpdateProfileImage(s3Url);
      } catch (error) {
        console.error('Failed to upload image:', error);
        setIsErrorAlert(true);
      } finally {
        setLoading(false);
      }
    }
  };
  const handleClose = () => {
    setIsOpen(false);
    setIsErrorAlert(false);
  };

  return (
    <div className="flex-col min-h-screen mobile:mb-8">
      <BackNavigation title="Profile"></BackNavigation>
      {isLoading && <span className="w-full text-center"> Loading....</span>}

      {isOpen && (
        <Success
          heading="Profile Updated"
          content=""
          buttonText="Ok"
          onOkClick={() => handleClose()}
          onClose={() => handleClose()}
        />
      )}
      {isErrorAlert && (
        <Error
          heading="Error"
          content=""
          buttonText="Ok"
          onOkClick={() => handleClose()}
          onClose={() => handleClose()}
        />
      )}

      <form
        className="flex mr-6 h-full px-2 mx-2 mobile:flex-col tablet:flex-col"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-80 laptop:w-74 mobile:w-full bg-white rounded-tr-3xl">
          <div className="flex flex-col bg-white rounded-xl max-w-[280px] items-center  p-4">
            <div className="flex space-x-2 ml-8">
              <div className="my-6 border-2 border-app-yellow rounded-full">
                <Avatar
                  src={profileImage}
                  fallback="?"
                  size="9"
                  color="amber"
                ></Avatar>
                <input
                  onChange={handleImageChange}
                  ref={inputRef}
                  type="file"
                  id="image"
                  className="hidden"
                  accept="image/*"
                />
              </div>
              <div
                onClick={() => {
                  if (inputRef.current !== null) inputRef.current.click();
                }}
              >
                <p className="text-white p-1 border-2 border-app-blue rounded-full bg-app-blue my-20 ">
                  <VscEdit />
                </p>
              </div>
            </div>

            <div>
              <h1 className="text-app-blue font-bold pt-2 text-base text-center">
                {getValues("firstName") + "  " + getValues("lastName")}
              </h1>
              <div>
                <div className="flex text-zinc-600 justify-center space-x-2">
                  <div className="py-1">
                    <AiOutlineMail />
                  </div>
                  <p> {email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="h-full p-2 text-app-gray-medium w-full flex justify-center">
          <div className="max-w-[1000px] w-full grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-2 desktop:grid-cols-2 largescreen:grid-cols-2 gap-4 laptop:gap-y-2">
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2 mobile:mt-2">
              <label htmlFor="firstName" className="text-sm mb-2">First Name*</label>
              <input
                {...register("firstName")}
                id="firstName"
                type="text"
                placeholder="Enter your First Name"
                className="rounded-xl border-2 h-8 p-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.firstName && (
                <p className="text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <label htmlFor="lastName" className="text-sm mb-2">Last Name*</label>
              <input
                {...register("lastName")}
                id="lastName"
                type="text"
                placeholder="Enter your Last Name"
                className="rounded-xl border-2 h-8 p-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.lastName && (
                <p className="text-red-500">{errors.lastName.message}</p>
              )}
            </div>
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <label htmlFor="ageGroup" className="text-sm mb-2">Age Group</label>
              <select
                {...register("ageGroup")}
                className="rounded-xl border-2 h-8 px-2 text-zinc-600 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                id="ageGroup"
                defaultValue={getValues("ageGroup") || ""}
              >
                <option value="">Select Age Group</option>
                {ageGroups.map((opt, index) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.ageRange}
                  </option>
                ))}
              </select>
              {errors.ageGroup && (
                <p className="text-red-500">{errors.ageGroup.message}</p>
              )}
            </div>
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <label htmlFor="city" className="text-sm mb-2">Town / City</label>
              <input
                {...register("city")}
                id="city"
                type="text"
                placeholder="Enter Town/City name "
                className="rounded-xl border-2 h-8 p-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.city && (
                <p className="text-red-500">{errors.city.message}</p>
              )}
            </div>
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <label htmlFor="country" className="text-sm mb-2">Country</label>
              <select
                {...register("country")}
                className="rounded-xl border-2 h-8 px-2 text-zinc-700 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                id="country"
                defaultValue={getValues("country") || ""}
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <p className="text-red-500">{errors.country.message}</p>
              )}
            </div>
            {/* <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <label htmlFor="website" className="text-sm mb-2">Website</label>
              <input
                {...register("website")}
                id="website"
                type="text"
                placeholder="Enter link ex: https://www.xyx.com"
                className="rounded-xl border-2 h-8 p-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.website && (
                <p className="text-red-500">{errors.website.message}</p>
              )}
            </div> */}
            {/* <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <label htmlFor="phone" className="text-sm mb-2">Contact Number</label>
              <input
                {...register("phone")}
                id="phone"
                type="number"
                placeholder="Enter your phone number"
                className="rounded-xl border-2 h-8 p-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.phone && (
                <p className="text-red-500">{errors.phone.message}</p>
              )}
            </div> */}
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2 mobile:mt-2">
              <label htmlFor="linkedinLink" className="text-sm mb-2">LinkedIn Profile URL</label>
              <input
                {...register("linkedIn")}
                id="linkedinLink"
                type="text"
                placeholder="Enter URL Eg. http://linkedin.com/xxxx"
                className="rounded-xl border-2 h-8 p-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.linkedIn && (
                <p className="text-red-500">{errors.linkedIn.message}</p>
              )}
            </div>
            {/* <div className="hidden tablet:block laptop:block desktop:block largescreen:block"></div> */}
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <div className="flex space-x-2">
                <label htmlFor="aboutMe" className="text-sm mb-2">About Me </label>
                <span className="opacity-50 text-sm"> (50 words)</span>
              </div>
              <textarea
                {...register("aboutMe")}
                id="aboutMe"
                rows={4}
                className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-xl border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="write here"
              ></textarea>
              {errors.aboutMe && (
                <p className="text-red-500">{errors.aboutMe.message}</p>
              )}
            </div>
            <div className="flex flex-col max-w-[280] min-w-[240px] mobile:col-span-2">
              <div className="flex space-x-2">
                {" "}
                <label htmlFor="profileMessage">
                  Areas of interest & Hobbies
                </label>
                <span className="opacity-50 text-sm"> (50 words)</span>
              </div>
              <textarea
                {...register("profileMessage")}
                id="profileMessage"
                rows={4}
                className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-xl border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="write here"
              ></textarea>
              {errors.profileMessage && (
                <p className="text-red-500">{errors.profileMessage.message}</p>
              )}
            </div>
            <div className="col-span-2 flex justify-start mt-4">
              <button
                type="submit"
                disabled={isLoading}
                className={`bg-app-blue ${isLoading ? "animate-bounce" : ""} text-base text-white w-48 h-8 rounded-xl outline-1`}
              >
                {isLoading ? "Updating..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Profile;